import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#preloadNeighbours, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleVideoSelection, { signal });
    this.#videoObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.#selectVideo(this.slideshow?.slides?.[this.slideshow.current || 0]);
      } else {
        this.#activeVideo?.pauseMedia();
        this.#activeVideo = null;
      }
    });
    this.#videoObserver.observe(this);
    // Start adjacent slides before any click or swipe, once the gallery approaches the viewport.
    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.#preloadNeighbours();
        this.#observer.disconnect();
      }
    }, { rootMargin: '400px' });
    this.#observer.observe(this);
  }

  #controller = new AbortController();
  #observer;
  #videoObserver;
  #activeVideo = null;

  #handleVideoSelection = (event) => {
    if (event.target !== this.slideshow) return;
    this.#selectVideo(event.detail.slide, event.detail.userInitiated);
  };

  #selectVideo(slide, userInitiated = false) {
    const video = slide?.querySelector('deferred-media[data-product-video]') ?? null;
    if (video === this.#activeVideo) return;
    this.#activeVideo?.pauseMedia();
    this.#activeVideo = video;
    video?.playProductMedia(userInitiated);
  }

  #preloadNeighbours = () => {
    const slides = this.slideshow?.slides;
    if (!slides?.length) return;
    const current = this.slideshow.current || 0;
    for (const offset of [-1, 0, 1]) {
      const slide = slides[(current + offset + slides.length) % slides.length];
      slide?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.setAttribute('loading', 'eager');
      });
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#observer?.disconnect();
    this.#videoObserver?.disconnect();
    this.#activeVideo?.pauseMedia();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
